import { SelectCategorieDepense } from '../components/shared/SelectCategorieDepense';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Wallet, ArrowLeftRight, Receipt, Landmark, Check, Camera, X, Download, ClipboardCheck } from '../lib/icons';
import { toast } from 'sonner';
import {
  fetchFinances,
  ajouterMouvement,
  ajouterReglement,
  validerReglement,
  fetchContacts,
  CAT_EXERCICE_ANTERIEUR,
  CATEGORIES_TIERS,
  MODES_PAIEMENT,
  emballagesDus,
  nomTiers,
} from '../lib/dataProvider';
import { compresserImage } from '../lib/image';
import { deposerDocument } from '../lib/docs';
import { PrintDoc } from '../components/print/PrintDoc';
import { Printer } from '../lib/icons';
import { fmt, PageTitle, StatutBadge } from '../components/shared/badges';
import { couleurSolde, couleurFlux } from '../lib/couleurs';
import { ActionsAdmin } from '../components/shared/ActionsAdmin';
import { RapprochementBancaire } from '../components/finances/RapprochementBancaire';
import { ComptesAssocies } from '../components/finances/ComptesAssocies';
import { RegistreCalamites } from '../components/finances/RegistreCalamites';
import { attribuerNumero } from '../lib/souches';
import { TransfertCoffreDialog } from '../components/finances/TransfertCoffreDialog';
import { ArreteCaisseJournalier } from '../components/finances/ArreteCaisseJournalier';
import { ArreteCoursier } from '../components/finances/ArreteCoursier';
import { exportComptable } from '../lib/exportComptable';
import { PeriodeFiltre, dansPeriode } from '../components/shared/PeriodeFiltre';
import { BanqueCanonique, FournisseursCanoniques } from '../components/canonique/BlocsCanoniques';
import { useAuth } from '../lib/auth';
import { caisseImposee } from '../lib/permissions';
import { useTresorerieVerrou } from '../lib/tresorerieEtat';
import { ContactCombobox } from '../components/shared/ContactCombobox';
import {
  adminModifierMouvement,
  adminSupprimerMouvement,
  adminModifierReglement,
  adminSupprimerReglement,
} from '../lib/dataProvider';

const inputCls =
  'h-10 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';
const TYPES = ['Alimentation', 'Dépense', 'Transfert'];

export default function Finances() {
  const [fin, setFin] = useState(null);
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const { user } = useAuth();
  const { verrou: tresorerieKo, titre: titreVerrou } = useTresorerieVerrou();
  const caisseFixe = caisseImposee(user?.role);
  const comptesAutorises = (liste) => (caisseFixe ? liste.filter((c) => c.id === caisseFixe) : liste);
  const verrou = Boolean(caisseFixe);

  const [mv, setMv] = useState({ date: aujourdhui, type: 'Alimentation', compteId: caisseFixe ?? 'esp', compteCibleId: '', categorie: '', libelle: '', piece: '', montant: '' });
  const [rg, setRg] = useState({ date: aujourdhui, categorie: 'Tiers', beneficiaire: '', tiersId: '', piece: '', mode: 'Espèces', compteId: caisseFixe ?? 'esp', montant: '', acompte: false, statut: 'Payé' });
  const [filtreTiers, setFiltreTiers] = useState('Tous');
  const [duMv, setDuMv] = useState('');
  const [arrete, setArrete] = useState(false);
  const [arreteJournalier, setArreteJournalier] = useState(false);
  const [auMv, setAuMv] = useState('');
  const [duRg, setDuRg] = useState('');
  const [auRg, setAuRg] = useState('');
  const [filtreType, setFiltreType] = useState('Tous');
  const [contacts, setContacts] = useState([]);
  const [apercu, setApercu] = useState(null);
  const [recu, setRecu] = useState(null);
  const [dp, setDp] = useState({ date: aujourdhui, categorie: '', beneficiaire: '', tiersId: '', compteId: caisseFixe ?? '', piece: '', libelle: '', montant: '', justificatif: null });

  const [onglet, setOnglet] = useState('tresorerie');
  const emballagesEnAttente = useMemo(() => (fin ? emballagesDus() : []), [fin]);
  const [transfert, setTransfert] = useState(false);
  const load = useCallback(() => {
    fetchFinances().then(setFin);
  }, []);
  useEffect(() => {
    load();
    fetchContacts().then(setContacts);
  }, [load]);

  const jour = useMemo(() => {
    if (!fin) return { entrees: 0, sorties: 0, reglements: 0 };
    const dates = fin.mouvements.map((m) => m.date).concat(fin.reglements.map((r) => r.date)).sort();
    const dernier = dates[dates.length - 1] ?? aujourdhui;
    const mvj = fin.mouvements.filter((m) => m.date === dernier);
    return {
      date: dernier,
      entrees: mvj.filter((m) => m.type === 'Alimentation').reduce((s, m) => s + m.montant, 0),
      sorties: mvj.filter((m) => m.type === 'Dépense' && m.categorie !== CAT_EXERCICE_ANTERIEUR).reduce((s, m) => s + m.montant, 0),
      exerciceAnterieur: mvj.filter((m) => m.categorie === CAT_EXERCICE_ANTERIEUR).reduce((s, m) => s + m.montant, 0),
      transferts: mvj.filter((m) => m.type === 'Transfert').reduce((s, m) => s + m.montant, 0),
      reglements: fin.reglements.filter((r) => r.date === dernier).reduce((s, r) => s + r.montant, 0),
    };
  }, [fin, aujourdhui]);

  if (!fin) return null;

  const nomCompte = (id) => fin.comptes.find((c) => c.id === id)?.nom ?? '—';
  const reglementsVisibles = fin.reglements.filter(
    (r) => (filtreTiers === 'Tous' || r.categorie === filtreTiers) && dansPeriode(r.date, duRg, auRg)
  );
  const mouvementsVisibles = fin.mouvements.filter(
    (m) => (filtreType === 'Tous' || m.type === filtreType) && dansPeriode(m.date, duMv, auMv)
  );

  const telecharger = (nom, head, body) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(
      new Blob([`\ufeff${[head.join(';'), ...body.map((l) => l.join(';'))].join('\n')}`], {
        type: 'text/csv;charset=utf-8;',
      })
    );
    a.download = nom;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(`Export CSV — ${body.length} ligne(s)`);
  };

  const exportJournal = () =>
    telecharger(
      duMv || auMv ? `journal-caisse_${duMv || 'debut'}_${auMv || 'fin'}.csv` : 'journal-caisse.csv',
      ['Date', 'Type', 'Compte', 'Compte cible', 'Catégorie', 'Libellé', 'N° pièce', 'Montant (TND)', 'Exercice'],
      mouvementsVisibles.map((m) => [
        m.date,
        m.type,
        fin.comptes.find((c) => c.id === m.compteId)?.nom ?? m.compteId,
        m.compteCibleId ? fin.comptes.find((c) => c.id === m.compteCibleId)?.nom ?? m.compteCibleId : '',
        m.categorie ?? '',
        (m.libelle ?? '').replace(/;/g, ','),
        m.piece ?? '',
        m.montant,
        m.categorie === CAT_EXERCICE_ANTERIEUR ? 'Exercice antérieur' : 'Exercice en cours',
      ])
    );

  const exportReglements = () =>
    telecharger(
      duRg || auRg ? `reglements_${duRg || 'debut'}_${auRg || 'fin'}.csv` : 'reglements.csv',
      ['Date', 'Catégorie', 'Bénéficiaire', 'N° pièce', 'Mode', 'Compte', 'Montant (TND)', 'Nature', 'Statut'],
      reglementsVisibles.map((r) => [
        r.date,
        r.categorie,
        (r.beneficiaire ?? nomTiers(r.tiersId) ?? '').replace(/;/g, ','),
        r.piece ?? '',
        r.mode ?? '',
        fin.comptes.find((c) => c.id === r.compteId)?.nom ?? r.compteId ?? '',
        r.montant,
        r.acompte ? 'Acompte' : 'Règlement',
        r.statut ?? '',
      ])
    );

  const parTiers = (() => {
    const map = {};
    reglementsVisibles.forEach((r) => {
      map[r.beneficiaire] = map[r.beneficiaire] ?? { total: 0, paye: 0, attente: 0, n: 0, categorie: r.categorie };
      map[r.beneficiaire].total += r.montant;
      map[r.beneficiaire].n += 1;
      if (r.statut === 'Payé') map[r.beneficiaire].paye += r.montant;
      else map[r.beneficiaire].attente += r.montant;
    });
    return Object.entries(map).sort((a, b) => b[1].total - a[1].total);
  })();

  const submitMv = async (e) => {
    e.preventDefault();
    const { error } = await ajouterMouvement({
      date: mv.date,
      type: mv.type,
      compteId: mv.compteId,
      compteCibleId: mv.type === 'Transfert' ? mv.compteCibleId : null,
      categorie: mv.type === 'Dépense' ? mv.categorie : null,
      libelle: mv.libelle,
      piece: mv.piece,
      montant: mv.montant,
    });
    if (error) return toast.error(error);
    toast.success(`${mv.type} enregistrée`);
    setMv({ ...mv, libelle: '', piece: '', montant: '', categorie: '', compteCibleId: '' });
    load();
  };

  const submitDp = async (e) => {
    e.preventDefault();
    if (!dp.compteId) return toast.error('Sélectionnez la caisse ou la source de trésorerie débitée.');
    const { error } = await ajouterMouvement({
      date: dp.date,
      type: 'Dépense',
      compteId: dp.compteId,
      compteCibleId: null,
      categorie: dp.categorie,
      beneficiaire: dp.beneficiaire.trim() || null,
      tiersId: dp.tiersId || null,
      libelle: dp.libelle,
      piece: dp.piece,
      justificatif: dp.justificatif,
      montant: dp.montant,
    });
    if (error) return toast.error(error);
    toast.success('Dépense enregistrée avec son justificatif');
    setDp({ date: aujourdhui, categorie: '', beneficiaire: '', tiersId: '', compteId: dp.compteId, piece: '', libelle: '', montant: '', justificatif: null });
    load();
  };

  const chargerJustificatif = async (file) => {
    if (!file) return;
    // Le fichier part au coffre-fort documentaire : la base ne garde que le lien.
    const { error, url, doublon, document: doc } = await deposerDocument(file, {
      source: 'justificatif_caisse',
      deposePar: user?.name,
    });
    if (error) return toast.error(error);
    setDp((p) => ({ ...p, justificatif: url, documentId: doc.id }));
    toast[doublon ? 'warning' : 'success'](
      doublon ? `Justificatif déjà archivé (${doc.nom}) — le document existant est réutilisé` : 'Justificatif attaché et archivé au coffre-fort'
    );
  };

  const submitRg = async (e) => {
    e.preventDefault();
    if (!rg.tiersId) return toast.error('Sélectionnez le bénéficiaire dans les contacts.');
    const { error } = await ajouterReglement(rg);
    if (error) return toast.error(error);
    toast.success('Règlement enregistré');
    setRg({ ...rg, beneficiaire: '', tiersId: '', piece: '', montant: '', acompte: false });
    load();
  };

  return (
    <div data-testid="finances-page" className="space-y-6">
      <PageTitle
        emoji="💰"
        titre="Finances & Trésorerie"
        sous="Caisses espèces et chèques, banque, dépenses catégorisées et règlements par tiers"
        action={
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setArreteJournalier(true)}
              data-testid="ouvrir-arrete-journalier"
              className="h-11 border border-slate-300 px-3 font-bold">
              Arrêté de caisse journalier
            </button>
            <button
              type="button"
              data-testid="ouvrir-transfert-coffre"
              onClick={() => setTransfert(true)}
              className="flex h-11 items-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700 sm:h-10"
            >
              <ArrowLeftRight size={16} /> Transfert inter-caisses
            </button>
            <button
              type="button"
              data-testid="ouvrir-arrete-coursier"
              onClick={() => setArrete(true)}
              className="flex h-11 items-center gap-2 border border-slate-300 px-3 font-heading text-sm font-bold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white sm:h-10"
            >
              <ClipboardCheck size={16} /> Arrêté coursier
            </button>
            <button
              type="button"
              data-testid="export-comptable"
              onClick={async () => {
                const r = await exportComptable({ du: duMv, au: auMv });
                toast.success(
                  `Classeur Excel généré (${r.onglets} onglets) : ${r.receptions} réception(s), ${r.ventes} vente(s), ${r.mouvements} mouvement(s), ${r.reglements} règlement(s), ${r.operations} dépense(s)`
                );
              }}
              className="flex h-11 items-center gap-2 border border-slate-300 px-3 font-heading text-sm font-bold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white sm:h-10"
            >
              <Download size={16} /> Export comptable Excel
            </button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-px bg-slate-200">
        {[
          ['tresorerie', 'Trésorerie & caisses', 'Caisses, dépenses et règlements'],
          ['cca', 'Comptes Courants d’Associés', 'Apports et remboursements de financement'],
          ['rapprochement', 'Rapprochement bancaire', 'Import de relevé CSV / Excel et lettrage'],
          ['canonique', 'Banque canonique (passerelle)', 'Extraits, transactions et affectations serveur'],
          ...(user?.role === 'Administrateur'
            ? [['calamites', 'Registre Calamités', 'Fonds dû, retenues et charge huilerie']]
            : []),
        ].map(([id, label, hint]) => (
          <button
            key={id}
            type="button"
            data-testid={`onglet-${id}`}
            onClick={() => setOnglet(id)}
            className={`px-4 py-2.5 text-left transition-colors duration-150 ${
              onglet === id ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <span className="block text-sm font-bold">{label}</span>
            <span className={`text-xs ${onglet === id ? 'text-slate-300' : 'text-slate-500'}`}>{hint}</span>
          </button>
        ))}
      </div>

      {onglet === 'rapprochement' && <RapprochementBancaire />}
      {onglet === 'cca' && <ComptesAssocies />}
      {onglet === 'canonique' && (
        <div data-testid="finances-canonique" className="space-y-4">
          <BanqueCanonique />
          <FournisseursCanoniques />
        </div>
      )}
      {onglet === 'calamites' && user?.role === 'Administrateur' && <RegistreCalamites />}

      <div className={onglet === 'tresorerie' ? 'space-y-6' : 'hidden'}>
      {emballagesEnAttente.length > 0 && (
        <div data-testid="alerte-emballages-finances" className="border-2 border-red-600 bg-red-50 px-4 py-3">
          <p className="flex items-center gap-2 font-heading text-base font-bold text-red-800">
            <Receipt size={16} /> Emballages à encaisser — {emballagesEnAttente.length} client(s) ·{' '}
            {fmt(emballagesEnAttente.reduce((s, e) => s + e.montant, 0), 3)} TND
          </p>
          <ul className="mt-2 space-y-1">
            {emballagesEnAttente.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 font-data text-sm text-red-900">
                <span>
                  {e.id} · {e.tiers} · {e.detail.map((d) => `${d.nom} × ${d.qte}`).join(', ')}
                </span>
                <span className="tabular font-semibold">{fmt(e.montant, 3)} TND</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 font-data text-xs text-red-800">
            Encaissement depuis la page Réception (bouton rouge « Emballage à régler » sur la ligne du lot).
          </p>
        </div>
      )}
      <div data-testid="soldes-comptes" className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {fin.soldes.map((c) => (
          <div
            key={c.id}
            data-testid={`compte-${c.id}`}
            className={`border border-slate-200 border-l-2 bg-white p-4 ${
              c.type === 'Banque' ? 'border-l-slate-900' : c.type === 'Chèques' ? 'border-l-sky-600' : 'border-l-emerald-600'
            }`}
          >
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
              {c.type === 'Banque' ? <Landmark size={13} /> : <Wallet size={13} />} {c.nom}
            </p>
            <p data-testid={`solde-${c.id}`} className={`tabular mt-3 font-data text-3xl font-medium tracking-tight ${couleurSolde(c.solde)}`}>
              {fmt(c.solde, 2)}
            </p>
            <p className="mt-1 font-data text-[11px] text-slate-500">
              TND · solde temps réel
              {c.dateOuverture ? ` · ouverture au ${c.dateOuverture} : ${fmt(c.initial ?? 0, 3)}` : ''}
              {c.decouvertAutorise ? ' · découvert autorisé' : ''}
            </p>
          </div>
        ))}
        <div data-testid="mouvements-jour" className="border border-slate-200 border-l-2 border-l-amber-600 bg-white p-4">
          <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
            Mouvements du {jour.date}
          </p>
          <dl className="mt-2 space-y-1 font-data text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-500">Entrées</dt>
              <dd data-testid="jour-entrees" className="tabular font-semibold text-emerald-700">+{fmt(jour.entrees, 2)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Dépenses</dt>
              <dd data-testid="jour-sorties" className="tabular font-semibold text-red-700">-{fmt(jour.sorties, 2)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Règlements</dt>
              <dd data-testid="jour-reglements" className="tabular font-semibold text-slate-900">{fmt(jour.reglements, 2)}</dd>
            </div>
            {jour.exerciceAnterieur > 0 && (
              <div className="flex justify-between border-t border-slate-100 pt-1">
                <dt className="text-slate-500">Exercice antérieur</dt>
                <dd data-testid="jour-exercice-anterieur" className="tabular font-semibold text-slate-500">
                  {fmt(jour.exerciceAnterieur, 2)}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      <section data-testid="section-banques" className="border border-slate-200 border-l-2 border-l-slate-900 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
            <Landmark size={16} /> Comptes bancaires (Attijari & Biatnet)
          </h2>
          <p className="text-xs text-slate-500">
            Soldes par banque et solde consolidé — interface prête pour la synchronisation des relevés
          </p>
        </div>
        <div className="grid grid-cols-1 gap-px bg-slate-200 md:grid-cols-3">
          {fin.soldes
            .filter((c) => c.type === 'Banque')
            .map((c) => (
              <div key={c.id} className="bg-white px-4 py-4">
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">{c.banque ?? c.nom}</p>
                <p data-testid={`banque-${c.id}`} className="tabular mt-2 font-data text-2xl font-medium text-slate-900">
                  {fmt(c.solde, 2)} <span className="text-sm text-slate-500">TND</span>
                </p>
                <p className="mt-1 font-data text-[11px] text-slate-500">{c.rib ?? '—'}</p>
              </div>
            ))}
          <div className="bg-slate-900 px-4 py-4">
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-300">Solde bancaire global</p>
            <p data-testid="banques-global-total" className="tabular mt-2 font-data text-2xl font-medium text-white">
              {fmt(
                fin.soldes.filter((c) => c.type === 'Banque').reduce((s, c) => s + c.solde, 0),
                2
              )}{' '}
              <span className="text-sm text-slate-400">TND</span>
            </p>
            <p className="mt-1 font-data text-[10px] uppercase tracking-[0.1em] text-slate-400">
              Synchronisation des flux : à connecter
            </p>
          </div>
        </div>
      </section>

      <section data-testid="section-caisse" className="border border-slate-200 border-l-2 border-l-emerald-600 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
              <ArrowLeftRight size={16} /> Journal de caisse & trésorerie
            </h2>
            <p className="text-xs text-slate-500">Alimentations, transferts entre caisses et dépenses catégorisées</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <PeriodeFiltre du={duMv} au={auMv} setDu={setDuMv} setAu={setAuMv} testid="periode-journal" total={fin.mouvements.length} filtres={mouvementsVisibles.length} />
            <button
              type="button"
              data-testid="journal-export-csv"
              onClick={exportJournal}
              className="flex h-10 items-center gap-2 border border-slate-300 px-3 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              <Download size={15} /> Exporter CSV
            </button>
          </div>
        </div>

        <form onSubmit={submitMv} className="grid grid-cols-1 gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 md:grid-cols-4 xl:grid-cols-7">
          <input data-testid="mv-date" type="date" value={mv.date} onChange={(e) => setMv({ ...mv, date: e.target.value })} className={inputCls} />
          <select data-testid="mv-type" value={mv.type} onChange={(e) => setMv({ ...mv, type: e.target.value })} className={inputCls}>
            {TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <select data-testid="mv-compte" value={mv.compteId} onChange={(e) => setMv({ ...mv, compteId: e.target.value })} disabled={verrou} className={`${inputCls} disabled:bg-slate-100`}>
            {comptesAutorises(fin.comptes).map((c) => (
              <option key={c.id} value={c.id}>{c.nom}</option>
            ))}
          </select>
          {mv.type === 'Transfert' ? (
            <select data-testid="mv-compte-cible" value={mv.compteCibleId} onChange={(e) => setMv({ ...mv, compteCibleId: e.target.value })} className={inputCls}>
              <option value="">— Vers quelle caisse —</option>
              {fin.comptes.map((c) => (
                <option key={c.id} value={c.id}>{c.nom}</option>
              ))}
            </select>
          ) : mv.type === 'Dépense' ? (
            <SelectCategorieDepense usage="depenses" data-testid="mv-categorie" value={mv.categorie} onChange={(e) => setMv({ ...mv, categorie: e.target.value })} className={inputCls}>
                <option value="">— Catégorie —</option>
              </SelectCategorieDepense>
          ) : (
            <input data-testid="mv-piece" placeholder="N° pièce" value={mv.piece} onChange={(e) => setMv({ ...mv, piece: e.target.value })} className={inputCls} />
          )}
          <input data-testid="mv-libelle" placeholder="Libellé" value={mv.libelle} onChange={(e) => setMv({ ...mv, libelle: e.target.value })} className={inputCls} />
          <input data-testid="mv-montant" type="number" step="0.01" placeholder="Montant TND" value={mv.montant} onChange={(e) => setMv({ ...mv, montant: e.target.value })} className={inputCls} />
          <button type="submit" data-testid="mv-submit" disabled={tresorerieKo} title={titreVerrou} className="flex h-10 items-center justify-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50">
            <Plus size={15} /> Enregistrer
          </button>
        </form>

        <div className="flex flex-wrap gap-px border-b border-slate-200 bg-slate-200">
          {['Tous', ...TYPES].map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`filtre-mv-${t}`}
              onClick={() => setFiltreType(t)}
              className={`px-3 py-2 text-xs font-semibold transition-colors duration-150 ${
                filtreType === t ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-caisse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Date', 'Type', 'Caisse / compte', 'Catégorie', 'Libellé', 'N° pièce', 'Justificatif', 'Montant (TND)', ''].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mouvementsVisibles.length === 0 ? (
                <tr data-testid="caisse-empty">
                  <td colSpan={8} className="px-3 py-10 text-center text-sm text-slate-500">Aucun mouvement.</td>
                </tr>
              ) : (
                mouvementsVisibles.map((m) => (
                  <tr key={m.id} data-testid={`caisse-row-${m.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-data text-sm text-slate-700">{m.date}</td>
                    <td className="px-3 py-2 text-sm text-slate-800">{m.type}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">
                      {nomCompte(m.compteId)}
                      {m.compteCibleId ? ` → ${nomCompte(m.compteCibleId)}` : ''}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-600">{m.categorie ?? '—'}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{m.libelle}</td>
                    <td className="px-3 py-2 font-data text-sm text-slate-600">{m.piece}</td>
                    <td className="px-3 py-2">
                      {m.justificatif ? (
                        <button
                          type="button"
                          data-testid={`justificatif-${m.id}`}
                          onClick={() => setApercu(m)}
                          className="border border-slate-300 p-1 transition-colors duration-150 hover:border-slate-900"
                        >
                          <img src={m.justificatif} alt="Justificatif" className="h-8 w-8 object-cover" />
                        </button>
                      ) : m.justificatifRef ? (
                        <span
                          data-testid={`justificatif-ref-${m.id}`}
                          title="Référence du document justificatif"
                          className="inline-block border border-sky-300 bg-sky-50 px-1.5 py-0.5 font-data text-[11px] font-bold text-sky-800"
                        >
                          {m.justificatifRef}
                        </span>
                      ) : (
                        <span className="font-data text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td
                      className={`tabular px-3 py-2 font-data text-sm font-semibold ${
                        m.type === 'Alimentation' ? couleurFlux('encaissement') : m.type === 'Dépense' ? couleurFlux('decaissement') : 'text-slate-900'
                      }`}
                    >
                      {m.type === 'Alimentation' ? '+' : m.type === 'Dépense' ? '-' : '↔ '}
                      {fmt(m.montant, 2)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <ActionsAdmin
                        id={m.id}
                        row={m}
                        titre="Corriger le mouvement de caisse"
                        champs={[
                          { key: 'date', label: 'Date', type: 'date' },
                          { key: 'montant', label: 'Montant (TND)', type: 'number' },
                          { key: 'categorie', label: 'Catégorie' },
                          { key: 'libelle', label: 'Libellé', large: true },
                          { key: 'piece', label: 'N° pièce' },
                        ]}
                        onModifier={(patch) => adminModifierMouvement(m.id, patch, user?.name)}
                        onSupprimer={() => adminSupprimerMouvement(m.id, user?.name)}
                        onDone={load}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section data-testid="section-depenses" className="border border-slate-200 border-l-2 border-l-red-600 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
            <Camera size={16} /> Dépenses & pièces justificatives
          </h2>
          <p className="text-xs text-slate-500">
            Facture, bon de livraison ou note de frais photographiée pour une traçabilité comptable totale
          </p>
        </div>

        <form onSubmit={submitDp} className="grid grid-cols-1 gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 md:grid-cols-4 xl:grid-cols-8">
          <input data-testid="dp-date" type="date" value={dp.date} onChange={(e) => setDp({ ...dp, date: e.target.value })} className={inputCls} />
          <SelectCategorieDepense usage="depenses" data-testid="dp-categorie" value={dp.categorie} onChange={(e) => setDp({ ...dp, categorie: e.target.value })} className={inputCls}>
                <option value="">— Catégorie —</option>
              </SelectCategorieDepense>
          <ContactCombobox
            contacts={contacts}
            value={dp.tiersId || ''}
            onChange={(id) => {
              const c = contacts.find((x) => x.id === id);
              setDp({ ...dp, tiersId: id, beneficiaire: c?.nom ?? '' });
            }}
            testid="dp-beneficiaire"
            placeholder="Bénéficiaire — contact"
          />
          <select data-testid="dp-compte" value={dp.compteId} onChange={(e) => setDp({ ...dp, compteId: e.target.value })} disabled={verrou} className={`${inputCls} disabled:bg-slate-100`}>
            {!verrou && <option value="">— Caisse / source débitée (obligatoire) —</option>}
            {comptesAutorises(fin.comptes).map((c) => (
              <option key={c.id} value={c.id}>{c.nom}</option>
            ))}
          </select>
          <input data-testid="dp-libelle" placeholder="Libellé / motif" value={dp.libelle} onChange={(e) => setDp({ ...dp, libelle: e.target.value })} className={inputCls} />
          <input data-testid="dp-piece" placeholder="N° pièce" value={dp.piece} onChange={(e) => setDp({ ...dp, piece: e.target.value })} className={inputCls} />
          <input data-testid="dp-montant" type="number" step="0.01" placeholder="Montant TND" value={dp.montant} onChange={(e) => setDp({ ...dp, montant: e.target.value })} className={inputCls} />
          <div className="flex gap-2">
            <label
              data-testid="dp-justificatif-label"
              className={`flex h-10 flex-1 cursor-pointer items-center justify-center gap-1.5 border px-2 text-xs font-semibold transition-colors duration-150 ${
                dp.justificatif ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              <Camera size={14} />
              {dp.justificatif ? 'Pièce OK' : 'Photo / fichier'}
              <input
                data-testid="dp-justificatif"
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => chargerJustificatif(e.target.files?.[0])}
              />
            </label>
            <button type="submit" data-testid="dp-submit" disabled={tresorerieKo} title={titreVerrou} className="flex h-10 items-center justify-center gap-2 bg-slate-900 px-3 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50">
              <Plus size={15} />
            </button>
          </div>
        </form>

        {dp.justificatif && (
          <div data-testid="dp-apercu" className="flex items-center gap-3 border-b border-slate-200 px-4 py-3">
            <img src={dp.justificatif} alt="Aperçu du justificatif" className="h-20 w-20 border border-slate-200 object-cover" />
            <button
              type="button"
              data-testid="dp-justificatif-retirer"
              onClick={() => setDp({ ...dp, justificatif: null })}
              className="border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              Retirer la pièce
            </button>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-depenses">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Date', 'Catégorie', 'Bénéficiaire', 'Décaissement', 'Libellé', 'N° pièce', 'Justificatif', 'Montant (TND)', ''].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fin.mouvements.filter((m) => m.type === 'Dépense').length === 0 ? (
                <tr data-testid="depenses-empty">
                  <td colSpan={8} className="px-3 py-10 text-center text-sm text-slate-500">Aucune dépense.</td>
                </tr>
              ) : (
                fin.mouvements
                  .filter((m) => m.type === 'Dépense')
                  .map((m) => (
                    <tr key={m.id} data-testid={`depense-row-${m.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 font-data text-sm text-slate-700">{m.date}</td>
                      <td className="px-3 py-2 text-sm text-slate-800">
                        {m.categorie ?? '—'}
                        {m.categorie === CAT_EXERCICE_ANTERIEUR && (
                          <span data-testid={`badge-exercice-${m.id}`} className="ml-1.5 border border-slate-300 bg-slate-100 px-1 py-0.5 font-data text-[10px] font-bold uppercase text-slate-600">
                            hors stats
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-slate-700">{m.beneficiaire ?? '—'}</td>
                      <td className="px-3 py-2 text-sm text-slate-600">{nomCompte(m.compteId)}</td>
                      <td className="px-3 py-2 text-sm text-slate-700">{m.libelle}</td>
                      <td className="px-3 py-2 font-data text-sm text-slate-600">{m.piece}</td>
                      <td className="px-3 py-2">
                        {m.justificatif ? (
                          <button
                            type="button"
                            data-testid={`depense-justificatif-${m.id}`}
                            onClick={() => setApercu(m)}
                            className="border border-slate-300 p-1 transition-colors duration-150 hover:border-slate-900"
                          >
                            <img src={m.justificatif} alt="Justificatif" className="h-8 w-8 object-cover" />
                          </button>
                        ) : (
                          <span className="font-data text-xs text-amber-700">manquant</span>
                        )}
                      </td>
                      <td className="tabular px-3 py-2 font-data text-sm font-semibold text-red-700">-{fmt(m.montant, 2)}</td>
                      <td className="px-3 py-2 text-right">
                        <ActionsAdmin
                          id={m.id}
                          row={m}
                          titre="Corriger la dépense"
                          champs={[
                            { key: 'date', label: 'Date', type: 'date' },
                            { key: 'montant', label: 'Montant (TND)', type: 'number' },
                            { key: 'categorie', label: 'Catégorie' },
                            { key: 'beneficiaire', label: 'Bénéficiaire' },
                            { key: 'libelle', label: 'Libellé', large: true },
                          ]}
                          onModifier={(patch) => adminModifierMouvement(m.id, patch, user?.name)}
                          onSupprimer={() => adminSupprimerMouvement(m.id, user?.name)}
                          onDone={load}
                        />
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section data-testid="section-reglements" className="border border-slate-200 border-l-2 border-l-slate-900 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
              <Receipt size={16} /> Règlements & acomptes par tiers
            </h2>
            <p className="text-xs text-slate-500">Fournisseurs, agriculteurs, clients et personnel</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <PeriodeFiltre du={duRg} au={auRg} setDu={setDuRg} setAu={setAuRg} testid="periode-reglements" total={fin.reglements.length} filtres={reglementsVisibles.length} />
            <button
              type="button"
              data-testid="reglements-export-csv"
              onClick={exportReglements}
              className="flex h-10 items-center gap-2 border border-slate-300 px-3 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              <Download size={15} /> Exporter CSV
            </button>
          </div>
        </div>

        <form onSubmit={submitRg} className="grid grid-cols-1 gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 md:grid-cols-4 xl:grid-cols-8">
          <input data-testid="rg-date" type="date" value={rg.date} onChange={(e) => setRg({ ...rg, date: e.target.value })} className={inputCls} />
          <select data-testid="rg-categorie" value={rg.categorie} onChange={(e) => setRg({ ...rg, categorie: e.target.value })} className={inputCls}>
            {CATEGORIES_TIERS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <div className="sm:col-span-2 lg:col-span-1">
            <ContactCombobox
              contacts={contacts}
              value={rg.tiersId || ''}
              onChange={(id) => {
                const c = contacts.find((x) => x.id === id);
                setRg({ ...rg, tiersId: id, beneficiaire: c?.nom ?? '' });
              }}
              testid="rg-beneficiaire"
              placeholder="Bénéficiaire — contact (nom, code CT-…)"
            />
          </div>
          <input data-testid="rg-piece" placeholder="N° pièce" value={rg.piece} onChange={(e) => setRg({ ...rg, piece: e.target.value })} className={inputCls} />
          <select data-testid="rg-mode" value={rg.mode} onChange={(e) => setRg({ ...rg, mode: e.target.value })} className={inputCls}>
            {MODES_PAIEMENT.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <select data-testid="rg-compte" value={rg.compteId} onChange={(e) => setRg({ ...rg, compteId: e.target.value })} disabled={verrou} className={`${inputCls} disabled:bg-slate-100`}>
            {comptesAutorises(fin.comptes).map((c) => (
              <option key={c.id} value={c.id}>{c.nom}</option>
            ))}
          </select>
          <input data-testid="rg-montant" type="number" step="0.01" placeholder="Montant TND" value={rg.montant} onChange={(e) => setRg({ ...rg, montant: e.target.value })} className={inputCls} />
          <div className="flex gap-2">
            <label className="flex h-10 flex-1 cursor-pointer items-center gap-2 border border-slate-300 bg-white px-2 text-xs font-semibold text-slate-700">
              <input data-testid="rg-acompte" type="checkbox" checked={rg.acompte} onChange={(e) => setRg({ ...rg, acompte: e.target.checked })} />
              Acompte
            </label>
            <button type="submit" data-testid="rg-submit" disabled={tresorerieKo} title={titreVerrou} className="flex h-10 items-center justify-center gap-2 bg-slate-900 px-3 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50">
              <Plus size={15} />
            </button>
          </div>
        </form>

        <div className="flex flex-wrap gap-px border-b border-slate-200 bg-slate-200">
          {['Tous', ...CATEGORIES_TIERS].map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`filtre-reglement-${c}`}
              onClick={() => setFiltreTiers(c)}
              className={`px-3 py-2 text-xs font-semibold transition-colors duration-150 ${
                filtreTiers === c ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-reglements">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Date', 'Catégorie', 'Bénéficiaire', 'N° pièce', 'Mode', 'Compte', 'Montant (TND)', 'Nature', 'Statut', ''].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reglementsVisibles.length === 0 ? (
                <tr data-testid="reglements-empty">
                  <td colSpan={10} className="px-3 py-10 text-center text-sm text-slate-500">
                    Aucun règlement pour cette catégorie.
                  </td>
                </tr>
              ) : (
                reglementsVisibles.map((r) => (
                  <tr key={r.id} data-testid={`reglement-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-data text-sm text-slate-700">{r.date}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{r.categorie}</td>
                    <td className="px-3 py-2 text-sm font-semibold text-slate-900">{r.beneficiaire}</td>
                    <td className="px-3 py-2 font-data text-sm text-slate-700">{r.piece}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{r.mode}</td>
                    <td className="px-3 py-2 text-sm text-slate-600">{nomCompte(r.compteId)}</td>
                    <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(r.montant, 2)}</td>
                    <td className="px-3 py-2">
                      <span className={`font-data text-[11px] uppercase tracking-[0.06em] ${r.acompte ? 'text-amber-700' : 'text-slate-500'}`}>
                        {r.acompte ? 'Acompte' : 'Solde'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {r.statut === 'Payé' ? (
                        <StatutBadge statut="Payé" />
                      ) : (
                        <button
                          type="button"
                          data-testid={`rg-valider-${r.id}`}
                          disabled={tresorerieKo}
                          title={titreVerrou}
                          onClick={async () => {
                            const { error } = await validerReglement(r.id);
                            if (error) return toast.error(error);
                            toast.success('Règlement payé');
                            load();
                          }}
                          className="inline-flex items-center gap-1 border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Check size={12} /> Payer
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        data-testid={`imprimer-reglement-${r.id}`}
                        onClick={() => setRecu(r)}
                        className="inline-flex items-center gap-1.5 border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                      >
                        <Printer size={13} /> PDF
                      </button>
                      <ActionsAdmin
                        id={r.id}
                        row={r}
                        titre="Corriger le règlement"
                        champs={[
                          { key: 'date', label: 'Date', type: 'date' },
                          { key: 'montant', label: 'Montant (TND)', type: 'number' },
                          { key: 'beneficiaire', label: 'Bénéficiaire' },
                          { key: 'piece', label: 'N° pièce' },
                          { key: 'notes', label: 'Notes', large: true },
                        ]}
                        onModifier={(patch) => adminModifierReglement(r.id, patch, user?.name)}
                        onSupprimer={() => adminSupprimerReglement(r.id, user?.name)}
                        onDone={load}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t border-slate-200">
          <p className="bg-slate-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
            Historique consolidé par tiers
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left" data-testid="table-par-tiers">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {['Tiers', 'Catégorie', 'Opérations', 'Payé (TND)', 'En attente (TND)', 'Total (TND)'].map((c) => (
                    <th key={c} className={th}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parTiers.map(([nom, v]) => (
                  <tr key={nom} data-testid={`tiers-row-${nom}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 text-sm font-semibold text-slate-900">{nom}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{v.categorie}</td>
                    <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{fmt(v.n)}</td>
                    <td className="tabular px-3 py-2 font-data text-sm text-emerald-700">{fmt(v.paye, 2)}</td>
                    <td className="tabular px-3 py-2 font-data text-sm text-amber-700">{fmt(v.attente, 2)}</td>
                    <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(v.total, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      {recu && (
        <PrintDoc
          type="Reçu de règlement"
          numero={attribuerNumero('recu_paiement', recu.id ?? recu.piece)}
          date={recu.date}
          tiers={recu.beneficiaire}
          lignes={[
            ['Catégorie de tiers', recu.categorie],
            ['Mode de paiement', recu.mode],
            ['Compte débité', nomCompte(recu.compteId)],
            ['Nature', recu.acompte ? 'Acompte' : 'Solde'],
            ['Statut', recu.statut],
          ]}
          totaux={[['Montant réglé', `${fmt(recu.montant, 2)} TND`]]}
          mentions="Reçu valant quittance pour le montant indiqué."
          onClose={() => setRecu(null)}
        />
      )}

      </div>

      {transfert && (
        <TransfertCoffreDialog
          comptes={fin.soldes}
          utilisateur={user?.name}
          onClose={() => setTransfert(false)}
          onDone={load}
        />
      )}

      {arreteJournalier && <ArreteCaisseJournalier
        comptes={comptesAutorises(fin.comptes)}
        onClose={() => setArreteJournalier(false)} />}
      {arrete && (
        <ArreteCoursier fin={fin} utilisateur={user?.name} onClose={() => setArrete(false)} />
      )}

      {apercu && (
        <div
          data-testid="apercu-justificatif"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4"
          onClick={() => setApercu(null)}
        >
          <div className="max-h-[90vh] w-full max-w-2xl overflow-auto border-2 border-white bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <p className="font-heading text-base font-bold text-slate-900">
                  Justificatif — {apercu.piece}
                </p>
                <p className="font-data text-xs text-slate-500">
                  {apercu.date} · {apercu.categorie ?? '—'} · {fmt(apercu.montant, 2)} TND
                </p>
              </div>
              <button
                type="button"
                data-testid="apercu-fermer"
                onClick={() => setApercu(null)}
                className="border border-slate-200 p-2 text-slate-500 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>
            <img src={apercu.justificatif} alt="Pièce justificative" className="w-full object-contain" />
          </div>
        </div>
      )}
    </div>
  );
}
